import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

// Leaflet's default marker icon resolves its image URLs relative to the CSS
// file, which breaks once a bundler hashes/moves those assets. Standard fix:
// point it at the actual bundled asset URLs instead. Runs once at module
// load, before any map is created.
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

// Riyadh — a reasonable default center/zoom when the screen has no location
// pinned yet, so the operator isn't dropped on "null island" (0,0).
const DEFAULT_CENTER: [number, number] = [24.7136, 46.6753]

// Interactive replacement for the old read-only Google-Maps iframe embed.
// Click anywhere to drop/move the pin — `onPick` fires immediately with the
// clicked coordinates so the caller can auto-save without a separate confirm
// step. OpenStreetMap tiles (keyless, no billing/API-key setup) via Leaflet,
// same "no API key, no JS bundle [dependency on Google]" spirit the old
// embed was built around, just genuinely interactive now.
export default function LocationMapPicker({
  lat, lng, onPick, saving,
}: {
  lat?: number | null
  lng?: number | null
  onPick: (lat: number, lng: number) => void
  saving?: boolean
}) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.Marker | null>(null)
  // Ref so the click handler (bound once, in the mount effect below) always
  // calls the LATEST onPick without needing to re-bind it on every parent
  // render. Written in its own effect rather than during render — a ref
  // write belongs in an effect/handler, not the render body.
  const onPickRef = useRef(onPick)
  useEffect(() => { onPickRef.current = onPick }, [onPick])
  // The coordinates this map last emitted from a click. The parent saves them
  // and feeds them straight back as props, which is indistinguishable from an
  // external change unless we remember what we sent.
  const selfPickRef = useRef<{ lat: number; lng: number } | null>(null)

  // Mount once. Re-creating the map on every coordinate change would fight
  // the operator's own pan/zoom mid-interaction.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const hasLocation = lat != null && lng != null
    const center: [number, number] = hasLocation ? [lat, lng] : DEFAULT_CENTER
    const map = L.map(containerRef.current, { attributionControl: false })
      .setView(center, hasLocation ? 15 : 4)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      subdomains: 'abc',
    }).addTo(map)
    L.control.attribution({ prefix: false, position: 'bottomright' }).addTo(map)

    if (hasLocation) {
      markerRef.current = L.marker(center).addTo(map)
    }

    map.on('click', (e: L.LeafletMouseEvent) => {
      if (markerRef.current) markerRef.current.setLatLng(e.latlng)
      else markerRef.current = L.marker(e.latlng).addTo(map)
      selfPickRef.current = { lat: e.latlng.lat, lng: e.latlng.lng }
      onPickRef.current(e.latlng.lat, e.latlng.lng)
    })

    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      markerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep the pin in sync if lat/lng change from outside (e.g. the parent
  // re-fetches the screen after a save, or the "Use my location" button
  // jumps straight to the browser's current position) without re-creating
  // the map. An externally-set location can be anywhere, so the view has to
  // follow it; zoom only ever goes IN, never out, so it doesn't fight an
  // operator who has deliberately zoomed out.
  //
  // A CLICK is not an external change, even though it arrives as one. The
  // parent saves the picked coordinates and passes them straight back down,
  // so this effect used to fire on every click and yank the map to zoom 15
  // centred on the new pin — click once at world zoom and the map slammed to
  // street level, losing the context you were aiming with. The pin is already
  // exactly where it was clicked, inside the visible area, so there is nothing
  // to recentre; skip the view change when the incoming coordinates are the
  // ones we just emitted. Epsilon rather than equality because the value makes
  // a round trip through the API before coming back.
  useEffect(() => {
    if (!mapRef.current || lat == null || lng == null) return
    const latlng = L.latLng(lat, lng)
    if (markerRef.current) markerRef.current.setLatLng(latlng)
    else markerRef.current = L.marker(latlng).addTo(mapRef.current)

    const self = selfPickRef.current
    selfPickRef.current = null
    const isOwnClick = self != null
      && Math.abs(self.lat - lat) < 1e-7
      && Math.abs(self.lng - lng) < 1e-7
    if (isOwnClick) return

    mapRef.current.setView(latlng, Math.max(mapRef.current.getZoom(), 15))
  }, [lat, lng])

  return (
    // `isolate` is load-bearing, not cosmetic. Leaflet's stylesheet assigns
    // z-indexes up to 1000 to its own panes and controls (.leaflet-top /
    // .leaflet-bottom are 1000, .leaflet-control 800), and it is imported
    // globally. `position: relative` alone does NOT open a stacking context,
    // so those children were competing directly with page-level overlays —
    // the map's tiles, marker and attribution painted straight through
    // anything layered above it. `isolation: isolate` opens a stacking
    // context on this wrapper, trapping every leaflet z-index inside the
    // map's own box where it belongs.
    <div className="relative isolate rounded-lg overflow-hidden border border-border">
      <div ref={containerRef} className="w-full h-44" />
      {saving && (
        <div className="absolute inset-0 bg-black/10 flex items-start justify-center pt-2 pointer-events-none">
          <span className="text-[11px] font-medium bg-card/95 text-foreground px-2 py-1 rounded-md shadow border border-border">
            {t('common.saving', 'Saving…')}
          </span>
        </div>
      )}
    </div>
  )
}
